import { useMemo } from 'react';
import { get, pick } from 'lodash-es';
import { inc, dec } from 'containers/Api2/reducer.js';
import useCurrentRole from 'containers/User/useCurrentRole.js';
import useAuthorizedRequest from 'containers/Api2/useAuthorizedRequest.js';
import { PENDING_STATUS, SCHEDULE_DRAFT, SCHEDULE_SCHEDULED, SCHEDULE_DELETE } from 'components/Post/constants.js';
import getSuccessMessage from 'components/interaction/PendingForm/getSuccessMessage.js';
import sameRole from 'utils/sameRole.js';
import useRecoveredData from 'utils/useRecoveredData.js';
import { addItem, deleteItem } from 'utils/useApi3/reduceObjects.js';
import { CONVERSATION_TYPE_GROUP } from '../../constants.jsx';
import useConversationsContext from '../../useConversationsContext.js';

function getDefaultValues(post, role) {
  const { media = [], newMedia = [], participants = [], title, ...rest } = post || {};
  return {
    files: { media, newMedia },
    body: get(post, ['latestMessages.body'], ''),
    title: title || '',
    ...rest,
    participants: participants.filter((p) => !p.archived && !p.isDeleted && !sameRole(p, role)),
  };
}

function useEditModal(post, onHide) {
  const role = useCurrentRole();
  const { baseUrl, inboxId, setInTopics } = useConversationsContext();
  const { authPost, authFetch } = useAuthorizedRequest();
  const [storedData, setStoredData] = useRecoveredData('topics', inboxId, post?.id || 'new');

  const defaultValues = useMemo(() => ({ ...getDefaultValues(post, role), ...storedData }), [post, role.uniqueId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleHide() {
    setStoredData(null);
    onHide();
  }

  function handleChange(values) {
    setStoredData(values);
  }

  function updateState(newStatus) {
    const orgStatus = post.status;
    if (orgStatus === newStatus) {
      return;
    }
    switch (orgStatus) {
      case SCHEDULE_DRAFT:
        setInTopics(['pending', 'draft'], dec);
        break;
      case SCHEDULE_SCHEDULED:
        setInTopics(['pending', 'scheduled'], dec);
        break;
      default:
        // Apparently a new item
    }
    switch (newStatus) {
      case SCHEDULE_DRAFT:
        setInTopics(['pending', 'draft'], inc);
        break;
      case SCHEDULE_SCHEDULED:
        setInTopics(['pending', 'scheduled'], inc);
        break;
      default:
    }
  }

  async function handleSubmit(data) {
    const { participants, files, status, topicType, scheduled } = data;
    const body = {
      id: post.id,
      postMedia: files.newMedia.map((o) => o.data),
      media: files.media,
      scheduled: scheduled?.toISOString(),
      topicType: topicType || CONVERSATION_TYPE_GROUP,
      ...pick(data, ['title', 'body', 'status']),
    };
    if (participants) {
      body.participants = participants.map((p) => pick(p, ['id', 'roleType']));
    }
    const message = await authPost({
      url: baseUrl,
      body,
      success: getSuccessMessage(status),
      withPromise: true,
    });

    if (post.id) {
      setInTopics(['pendingItems'], deleteItem(post.id));
    }
    const selector = PENDING_STATUS.includes(status) ? ['pendingItems'] : [];
    setInTopics(selector, addItem(message, true));
    setStoredData(null);
    updateState(status);
    handleHide();
  }

  async function handleDelete() {
    const selector = PENDING_STATUS.includes(post.status) ? ['pendingItems'] : [];
    await authFetch({ method: 'DELETE', url: `${baseUrl}/${post.id}` });
    setInTopics(selector, deleteItem(post.id));
    updateState(SCHEDULE_DELETE);
    handleHide();
  }

  return { defaultValues, handleSubmit, handleDelete, handleHide, handleChange, inboxId };
}
export default useEditModal;
